import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { PsychrometricCalculator } from '../../utils/psychrometric';
import { capturePsychrometricChart } from '../../utils/imageCapture';
import StatePointsInfo from './StatePointsInfo';

interface PsychrometricPoint {
  name: string;
  dryBulbTemp: number;
  relativeHumidity?: number;
  humidityRatio?: number;
  color: string;
  size?: number;
  labelPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  labelOffset?: { x?: number; y?: number };  // 라벨 오프셋 (픽셀)
}

interface ProcessLine {
  from: string; // 시작점 이름
  to: string;   // 끝점 이름
  color: string;
  width?: number;
  label?: string;
  type?: 'mixing' | 'cooling' | 'heating' | 'process' | 'bypass'; // 프로세스 타입
}

interface PsychrometricChartProps {
  points: PsychrometricPoint[];
  lines?: ProcessLine[];
  _width?: number; // unused, for eslint/ts compatibility
  _height?: number; // unused, for eslint/ts compatibility
  elevation?: number;
  showReferenceLines?: boolean; // 참조선 표시 여부
  showValueSymbols?: boolean;   // 값 기호 표시 여부
  _showRelativeHumidity?: boolean; // unused, for eslint/ts compatibility
  showEnthalpy?: boolean;       // 엔탈피 선 표시 여부
  winterOutdoorTemp?: number;   // 겨울철 외기온도 (차트 x축 최소값 계산용)
  summerOutdoorTemp?: number;   // 여름철 외기온도 (차트 x축 최대값 계산용)
  raFanType?: 'None' | 'Inside' | 'Outside'; // RA Fan 타입
  humidifierType?: string;      // 가습기 타입 ('Steam(O)'일 때 외부 설치)
  preheatingCoilType?: string;  // 예열코일 타입 (PH)
  reheatingCoilType?: string;   // 재열코일 타입 (RH)
  isCoolingMode?: boolean;      // 냉방 모드 여부 (냉방 현열/잠열 부하 > 0.001)
  raLightingLoadMode?: 'separate' | 'combined'; // RA Lighting Load 모드 ('separate': RA 온도상승 적용)
  // 풍량 값들 (CMH 단위)
  saFlow?: number;              // 급기량 (SA)
  raFlow?: number;              // 환기량 (RA)
  eaFlow?: number;              // 배기량 (EA)
  taFlow?: number;              // 전체 배기량 (TA = EA + 가압량)
  oaFlowMin?: number;           // 외기량 최소값 (OA Min)
  oaFlowMax?: number;           // 외기량 최대값 (OA Max)
  minTemp?: number;             // X축 최소값 (건구온도)
  maxTemp?: number;             // X축 최대값 (건구온도)
  chartId?: string;             // 엑셀 이미지 캡처용 ID
  showStatePoints?: boolean;    // 상태점 정보 표시 여부 (기본값: true)
  onChartCaptured?: (data: {
    image: string;
    points: PsychrometricPoint[];
    lines: ProcessLine[];
    metadata: any;
  }) => void;                   // 차트 캡처 완료 시 콜백
}

const PsychrometricChart: React.FC<PsychrometricChartProps> = ({
  points,
  lines = [],
  _width = 1400, // A4 출력 고려 폭
  _height = 1000, // A4 출력 고려 높이
  elevation = 0,
  showReferenceLines = false,
  showValueSymbols = false,
  _showRelativeHumidity = true,
  showEnthalpy = false,
  winterOutdoorTemp,
  summerOutdoorTemp,
  raFanType = 'Inside',  // 기본값은 'Inside' (RF 표시)
  humidifierType = 'None', // 기본값은 'None'
  preheatingCoilType = 'None', // 예열코일 기본값
  reheatingCoilType = 'None',  // 재열코일 기본값
  isCoolingMode = true,  // 기본값: 냉방 모드
  raLightingLoadMode = 'combined', // 기본값은 'combined' (RA 온도상승 미적용)
  // 풍량 값들 (기본값 설정)
  saFlow = 25600,          // 급기량 기본값
  raFlow = 19300,          // 환기량 기본값
  eaFlow = 5300,           // 배기량 기본값
  taFlow,                  // 전체 배기량 (EA + 가압량)
  oaFlowMin = 2400,        // 외기량 최소값 기본값
  oaFlowMax = 25600,       // 외기량 최대값 기본값
  minTemp,
  maxTemp,
  chartId,
  showStatePoints = true,  // 기본값: 상태점 정보 표시
  onChartCaptured
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null); // 이미지 캡처 전용 Canvas (고정 크기)
  const capturedRef = useRef<boolean>(false); // 중복 캡처 방지

  // 로컬스토리지에서 체크박스 상태 로드
  const [showGrid, setShowGrid] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showGrid');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [showLabels, setShowLabels] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showLabels');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [internalShowReferenceLines, setInternalShowReferenceLines] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showReferenceLines');
    return saved !== null ? JSON.parse(saved) : false; // 기본값 false
  });

  const [internalShowValueSymbols, setInternalShowValueSymbols] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showValueSymbols');
    return saved !== null ? JSON.parse(saved) : false; // 기본값 false
  });

  const [internalShowRelativeHumidity, setInternalShowRelativeHumidity] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showRelativeHumidity');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [internalShowEnthalpy, setInternalShowEnthalpy] = useState(() => {
    const saved = localStorage.getItem('psychrometric-showEnthalpy');
    return saved !== null ? JSON.parse(saved) : showEnthalpy;
  });

  // chartDimensions: 표시용 Canvas 크기 (반응형)
  const [chartDimensions, setChartDimensions] = useState({ width: 2400, height: 1800 });

  // captureDimensions: 이미지 캡처용 Canvas 크기 (고정, showStatePoints와 무관)
  // A4 출력 최적화를 위한 고정 크기 (4:3 종횡비 유지)
  const captureDimensions = { width: 1200, height: 900 };

  const containerRef = useRef<HTMLDivElement>(null);

  // 차트 크기 설정: 표시용 Canvas는 반응형 크기 (4:3 종횡비 유지)
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const containerWidth = Math.max(300, Math.floor(rect.width));
        const containerHeight = Math.max(300, Math.floor(rect.height));

        // 4:3 비율 유지 (A4 가로 방향 최적화)
        const aspectRatio = 4 / 3;
        let width = containerWidth;
        let height = Math.floor(width / aspectRatio);

        // 높이가 컨테이너를 초과하면 높이 기준으로 재조정
        if (height > containerHeight) {
          height = containerHeight;
          width = Math.floor(height * aspectRatio);
        }

        setChartDimensions({ width, height });
      }
    };

    updateDimensions();

    // ResizeObserver로 부모 크기 변화 감지
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(containerRef.current);
    } else {
      // fallback: window resize
      window.addEventListener('resize', updateDimensions);
    }

    return () => {
      if (resizeObserver && containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // 레이아웃 설정 상수 (모든 캔버스에 공통)
  // layoutMargin: 캔버스 전체 여백 (차트 주변 적절한 공간)
  // chartMargin: 차트 내부 여백 (축, 라벨, 제목을 위한 공간)
  const layoutMargin = { top: 30, right: 10, bottom: 30, left: 10 };
  const chartMargin = { top: 20, right: 85, bottom: 48, left: 50 };

  // 상태점들의 건구온도 범위를 찾아서 온도 범위 설정
  const findTemperatureRange = () => {
    // minTemp, maxTemp가 props로 전달되면 우선 사용
    if (typeof minTemp === 'number' && typeof maxTemp === 'number') {
      return { min: minTemp, max: maxTemp };
    }
    if (points.length === 0) {
      // 상태점이 없는 경우 기본값 사용
      return {
        min: winterOutdoorTemp !== undefined ? Math.floor(winterOutdoorTemp - 5) : 0,
        max: summerOutdoorTemp !== undefined ? Math.ceil(summerOutdoorTemp + 5) : 50
      };
    }

    // 상태점들에서 최대/최소 온도 찾기
    const temperatures = points.map(point => point.dryBulbTemp).filter(temp =>
      temp !== undefined && temp !== null && !isNaN(temp) && isFinite(temp)
    );

    if (temperatures.length === 0) {
      // 유효한 온도가 없는 경우 기본값 사용
      return {
        min: winterOutdoorTemp !== undefined ? Math.floor(winterOutdoorTemp - 5) : 0,
        max: summerOutdoorTemp !== undefined ? Math.ceil(summerOutdoorTemp + 5) : 50
      };
    }

    const minPointTemp = Math.min(...temperatures);
    const maxPointTemp = Math.max(...temperatures);

    // 최소온도: 최소 상태점 온도 - 5°C (여유값)
    // 최대온도: 최대 상태점 온도 + 5°C (사용자 요청)
    return {
      min: Math.floor(minPointTemp - 5),
      max: Math.ceil(maxPointTemp + 5)
    };
  };

  // 렌더링 컨텍스트 타입 정의
  interface DrawingContext {
    chartWidth: number;
    chartHeight: number;
    tempRange: { min: number; max: number };
    humidityRange: { min: number; max: number };
    chartArea: { x: number; y: number; width: number; height: number };
    diagramArea: { x: number; y: number; width: number; height: number };
    getCanvasCoords: (temp: number, humidity: number) => { x: number; y: number };
  }

  // 현재 렌더링 컨텍스트를 저장 (drawing functions에서 참조)
  let currentDrawingContext: DrawingContext | null = null;

  const drawChart = (targetCanvas?: HTMLCanvasElement, dimensions?: { width: number; height: number }) => {
    // 렌더링 대상 Canvas와 크기 결정
    const canvas = targetCanvas || canvasRef.current;
    const dims = dimensions || chartDimensions;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 캔버스 크기를 직접 설정 (devicePixelRatio 무시)
    canvas.width = dims.width;
    canvas.height = dims.height;

    // 캔버스 초기화
    ctx.clearRect(0, 0, dims.width, dims.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, dims.width, dims.height);

    // 레이아웃 계산 (dims 기준)
    const totalWidth = dims.width - layoutMargin.left - layoutMargin.right;
    const totalHeight = dims.height - layoutMargin.top - layoutMargin.bottom;

    const chartArea = {
      x: layoutMargin.left,
      y: layoutMargin.top,
      width: totalWidth,
      height: totalHeight
    };

    // 시스템 다이어그램 영역
    const diagramWidth = Math.min(465, Math.max(300, totalWidth * 0.6));
    const diagramHeight = 240;

    const diagramArea = {
      x: chartArea.x + 10,
      y: chartArea.y + 10,
      width: diagramWidth,
      height: diagramHeight
    };

    // 차트 크기 계산: 사용 가능한 공간을 최대한 활용
    const maxChartWidth = chartArea.width - chartMargin.left - chartMargin.right;
    const maxChartHeight = chartArea.height - chartMargin.top - chartMargin.bottom;

    // 온도 범위 계산
    const temperatureRange = findTemperatureRange();
    const chartMinTemp = temperatureRange.min;
    const chartMaxTemp = temperatureRange.max;
    const tempRange = { min: chartMinTemp, max: chartMaxTemp };
    const humidityRange = { min: 0, max: 30 };

    // 사용 가능한 공간을 최대한 활용 (1:1 물리적 비율 대신)
    const chartWidth = maxChartWidth;
    const chartHeight = maxChartHeight;

    // 좌표 변환 함수 (translate 후 0,0 기준)
    const getCanvasCoords = (temp: number, humidity: number) => {
      const x = ((temp - tempRange.min) / (tempRange.max - tempRange.min)) * chartWidth;
      const y = chartHeight - ((humidity - humidityRange.min) / (humidityRange.max - humidityRange.min)) * chartHeight;
      return { x, y };
    };

    // 현재 렌더링 컨텍스트 설정 (drawing functions에서 참조)
    currentDrawingContext = {
      chartWidth,
      chartHeight,
      tempRange,
      humidityRange,
      chartArea,
      diagramArea,
      getCanvasCoords
    };

    // 레이아웃 그리기
    ctx.save();
    // 습공기 선도 영역으로 좌표계 변환
    ctx.translate(chartArea.x + chartMargin.left, chartArea.y + chartMargin.top);

    // 축 그리기
    drawAxes(ctx);

    // 그리드 및 선 그리기
    if (showGrid) {
      drawDryBulbTemperatureLines(ctx);
      drawAbsoluteHumidityLines(ctx);
    }

    // 상대습도 곡선 그리기 (100% RH는 항상 표시)
    drawRelativeHumidityCurves(ctx);

    // 엔탈피 선 그리기
    if (internalShowEnthalpy) {
      drawEnthalpyLines(ctx);
    }

    // 참조선 그리기 (프로세스 선보다 먼저)
    if (internalShowReferenceLines) {
      drawReferenceLines(ctx);
    }

    // 엔탈피선 체크 시 엔탈피 경계선도 함께 표시
    if (internalShowEnthalpy) {
      drawEnthalpyBoundary(ctx);
    }

    // 프로세스 선 그리기 (참조선 위, 상태점 아래)
    drawProcessLines(ctx);

    // 상태점 그리기 (가장 위)
    drawPoints(ctx);

    // 값 기호 그리기 (상태점 후에)
    if (internalShowValueSymbols) {
      drawValueSymbols(ctx);
    }

    ctx.restore();

    // 4. 선도 영역 경계선 그리기
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    ctx.strokeRect(chartArea.x, chartArea.y, chartArea.width, chartArea.height);

    // 5. 시스템 다이어그램을 습공기 선도 좌측 상단에 오버레이
    drawSystemDiagram(ctx);
  };

  const drawAxes = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.5;
    ctx.font = '16px Arial';
    ctx.fillStyle = '#000000';

    // X축 (온도) - translate된 좌표계에서 0,0 기준
    ctx.beginPath();
    ctx.moveTo(0, chartHeight);
    ctx.lineTo(chartWidth, chartHeight);
    ctx.stroke();

    // Y축 (습도) - 오른쪽 축
    ctx.beginPath();
    ctx.moveTo(chartWidth, 0);
    ctx.lineTo(chartWidth, chartHeight);
    ctx.stroke();

    // X축 라벨 및 눈금 (5도 단위)
    ctx.font = '13px Arial';
    ctx.textAlign = 'center';
    for (let temp = tempRange.min; temp <= tempRange.max; temp += 1) {
      if (temp % 5 === 0) {
        const { x } = getCanvasCoords(temp, humidityRange.min);
        ctx.fillText(`${temp}`, x, chartHeight + 18);
      }
    }

    // Y축 라벨 및 눈금
    ctx.textAlign = 'left';
    for (let humidity = humidityRange.min; humidity <= humidityRange.max; humidity += 5) {
      const { y } = getCanvasCoords(tempRange.min, humidity);
      ctx.fillText(`${humidity}`, chartWidth + 8, y + 4);
    }

    // X축 제목 (라벨과 25px 간격)
    ctx.font = '13px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('건구온도 (°C)', chartWidth / 2, chartHeight + 43);

    // Y축 제목 (회전)
    ctx.save();
    ctx.translate(chartWidth + 60, chartHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('절대습도 (g/kg)', 0, 0);
    ctx.restore();

    ctx.restore();
  };

  const drawDryBulbTemperatureLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartHeight, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    // 5도 단위로 굵은 선 표시
    for (let temp = tempRange.min; temp <= tempRange.max; temp += 1) {
      const isMajorLine = temp % 5 === 0;
      ctx.strokeStyle = isMajorLine ? '#000000' : '#e0e0e0';
      ctx.lineWidth = isMajorLine ? 0.7 : 0.5;
      if (temp === tempRange.min) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
      }

      let maxHumidity = 0;
      try {
        const hr = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);
        maxHumidity = hr * 1000;
      } catch (_e) { continue; }

      if (maxHumidity > humidityRange.min) {
        const start = getCanvasCoords(temp, humidityRange.min);
        const end = getCanvasCoords(temp, Math.min(maxHumidity, humidityRange.max));
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  };

  const drawAbsoluteHumidityLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    for (let humidity = humidityRange.min + 1; humidity <= humidityRange.max; humidity += 1) {
      if (humidity <= 0) continue;

      if (humidity === humidityRange.max) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
      } else {
        const isMajorLine = humidity % 5 === 0;
        ctx.strokeStyle = isMajorLine ? '#000000' : '#e0e0e0';
        ctx.lineWidth = isMajorLine ? 0.7 : 0.5;
      }

      let saturationTemp = tempRange.max;
      try {
        saturationTemp = PsychrometricCalculator.findDryBulbTempFromHumidityRatio(humidity / 1000, 100, elevation);
      } catch (_e) { continue; }

      // 선이 차트 범위 내에서 시작하도록 보장
      const startTemp = Math.max(saturationTemp, tempRange.min);

      if (startTemp < tempRange.max) {
        const start = getCanvasCoords(startTemp, humidity);
        const end = getCanvasCoords(tempRange.max, humidity);
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  };

  const drawRelativeHumidityCurves = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    const rhValues = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

    rhValues.forEach(rh => {
      // 100% RH는 항상 표시하고 검정색, 다른 선들은 체크박스 상태에 따라
      if (rh === 100) {
        ctx.strokeStyle = '#000000'; // 100% RH는 검정색
        ctx.lineWidth = 2.5;
      } else if (internalShowRelativeHumidity) {
        ctx.strokeStyle = '#2d8659'; // 다른 상대습도선들은 어두운 초록색
        ctx.lineWidth = 0.6;
      } else {
        return; // 체크박스 해제시 100% 외에는 건너뛰기
      }
      ctx.beginPath();

      let firstPoint = true;
      let lastValidPoint = null;

      for (let temp = tempRange.min; temp <= tempRange.max; temp += 0.5) {
        try {
          const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, rh, elevation);
          const humidityGrams = humidityRatio * 1000;

          if (humidityGrams >= humidityRange.min && humidityGrams <= humidityRange.max) {
            const { x, y } = getCanvasCoords(temp, humidityGrams);
            lastValidPoint = { x, y, temp, humidityGrams };

            if (firstPoint) {
              ctx.moveTo(x, y);
              firstPoint = false;
            } else {
              ctx.lineTo(x, y);
            }
          }
        } catch (_error) { /* ignore */ }
      }

      // 상대습도선이 최대 절대습도선과 교차하는 점 찾기
      if (lastValidPoint) {
        // 최대 절대습도(30 g/kg)에서 해당 상대습도의 온도 찾기
        let intersectionTemp = tempRange.max;
        try {
          // 이진 탐색으로 교차점의 온도 찾기
          let minTemp = lastValidPoint.temp;
          let maxTemp = tempRange.max;

          for (let i = 0; i < 20; i++) { // 충분한 정밀도로 반복
            const midTemp = (minTemp + maxTemp) / 2;
            try {
              const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(midTemp, rh, elevation);
              const humidityGrams = humidityRatio * 1000;

              if (Math.abs(humidityGrams - humidityRange.max) < 0.01) {
                intersectionTemp = midTemp;
                break;
              } else if (humidityGrams < humidityRange.max) {
                minTemp = midTemp;
              } else {
                maxTemp = midTemp;
              }
            } catch (_e) {
              maxTemp = midTemp;
            }
          }

          // 교차점까지 선 그리기
          if (intersectionTemp > lastValidPoint.temp) {
            const { x: endX, y: endY } = getCanvasCoords(intersectionTemp, humidityRange.max);
            ctx.lineTo(endX, endY);
          }
        } catch (_error) {
          // 계산 실패시 최대 온도까지만 그리기
        }
      }

      ctx.stroke();

      // RH 라벨
      if (showLabels && rh < 100 && rh % 20 === 0) {
        const labelTemp = 45;
        try {
          const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(labelTemp, rh, elevation);
          const humidityGrams = humidityRatio * 1000;
          if (humidityGrams <= humidityRange.max && humidityGrams >= humidityRange.min) {
            const { x, y } = getCanvasCoords(labelTemp, humidityGrams);
            if (x < chartWidth) {
              ctx.fillStyle = '#0066cc';
              ctx.font = '16px Arial'; // 12px → 16px
              ctx.fillText(`${rh}%`, x + 5, y);
            }
          }
        } catch (_error) { /* 라벨 표시 오류 시 건너뛰기 */ }
      }
    });
    ctx.restore();
  };



  const drawEnthalpyLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();

    // 엔탈피 보조 경계선의 시작점과 끝점 계산 (교차점 계산에 사용)
    let boundaryStartHumidity = 0;
    try {
      const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(tempRange.min, 100, elevation);
      boundaryStartHumidity = satHumidityRatio * 1000 + 1;
    } catch (_error) {
      const saturationPressure = 610.78 * Math.exp(17.27 * tempRange.min / (tempRange.min + 237.3));
      const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
      const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
      boundaryStartHumidity = satHumidityRatio * 1000 + 1;
    }

    let boundaryEndTemp = tempRange.max - 1;
    const boundaryEndHumidity = humidityRange.max;
    for (let temp = tempRange.max; temp >= tempRange.min; temp -= 0.5) {
      let satHumidity = 0;
      try {
        const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);
        satHumidity = satHumidityRatio * 1000;
      } catch (_error) {
        const saturationPressure = 610.78 * Math.exp(17.27 * temp / (temp + 237.3));
        const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
        const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
        satHumidity = satHumidityRatio * 1000;
      }
      if (satHumidity <= humidityRange.max) {
        boundaryEndTemp = temp - 1;
        break;
      }
    }

    // 엔탈피 보조선: (tempRange.min, boundaryStartHumidity) → (boundaryEndTemp, boundaryEndHumidity)
    // 직선 방정식: humidity = boundaryStartHumidity + (boundaryEndHumidity - boundaryStartHumidity) * (temp - tempRange.min) / (boundaryEndTemp - tempRange.min)

    // 엔탈피 선 (대각선) - 온도 및 습도 범위 기반으로 동적 계산
    // 엔탈피 공식: h = t + x*(2501 + 1.86*t) (kJ/kg)
    // 최소 엔탈피: 최소온도, 최소습도
    const minEnthalpy = tempRange.min + (humidityRange.min / 1000) * (2501 + 1.86 * tempRange.min);
    // 최대 엔탈피: 최대온도, 최대습도
    const maxEnthalpy = tempRange.max + (humidityRange.max / 1000) * (2501 + 1.86 * tempRange.max);

    // 5 kJ/kg 단위로 엔탈피 값 생성
    const enthalpyStep = 5;
    const enthalpyStart = Math.floor(minEnthalpy / enthalpyStep) * enthalpyStep;
    const enthalpyEnd = Math.ceil(maxEnthalpy / enthalpyStep) * enthalpyStep;

    const enthalpyValues: number[] = [];
    for (let h = enthalpyStart; h <= enthalpyEnd; h += enthalpyStep) {
      enthalpyValues.push(h);
    }

    enthalpyValues.forEach((enthalpy, _index) => {

      // 10 단위는 굵게, 20 단위는 더 굵게
      if (enthalpy % 20 === 0) {
        ctx.strokeStyle = '#9c27b0';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]);
      } else if (enthalpy % 10 === 0) {
        ctx.strokeStyle = '#9c27b0';
        ctx.lineWidth = 1.2;
        ctx.setLineDash([]);
      } else {
        ctx.strokeStyle = '#ce93d8';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 2]);
      }
      ctx.beginPath();

      let firstPoint = true;
      let pointCount = 0;

      // 엔탈피 선은 Y축(최소온도)부터 엔탈피 경계선까지 그리기
      // 엔탈피 공식: h = t + x*(2501 + 1.86*t) (kJ/kg)

      // 각 온도에서 엔탈피 값에 해당하는 습도 계산하여 선 그리기
      for (let temp = tempRange.min; temp <= tempRange.max; temp += 0.2) {
        // x = (h - t) / (2501 + 1.86*t)
        const humidityRatio = Math.max(0, (enthalpy - temp) / (2501 + 1.86 * temp));
        const humidityGrams = humidityRatio * 1000;

        // 엔탈피 보조선상의 습도 계산 (경계선 체크)
        const boundaryHumidity = boundaryStartHumidity +
          (boundaryEndHumidity - boundaryStartHumidity) * (temp - tempRange.min) / (boundaryEndTemp - tempRange.min);

        // 차트 범위 내 확인: Y축(humidityRange.min)부터 엔탈피 보조선 또는 최대습도(humidityRange.max)까지
        const maxAllowedHumidity = Math.min(boundaryHumidity, humidityRange.max);

        if (humidityGrams >= humidityRange.min && humidityGrams <= maxAllowedHumidity) {
          const { x, y } = getCanvasCoords(temp, humidityGrams);

          if (firstPoint) {
            ctx.moveTo(x, y);
            firstPoint = false;
          } else {
            ctx.lineTo(x, y);
          }
          pointCount++;
        }
      }


      if (pointCount > 0) {
        ctx.stroke();
      }

      // 엔탈피 라벨 (10 단위로, 엔탈피 보조선과 만나는 지점에 표시)
      if (showLabels && enthalpy % 10 === 0) {
        // 엔탈피 보조선과 가장 가까운 점과 그 전 점 찾기
        let closestPoint = null;
        let beforeClosestPoint = null;
        let minDistance = Infinity;
        let prevCoords = null;

        // 엔탈피 선을 따라 탐색하여 보조선에 가장 가까운 점 찾기
        for (let temp = tempRange.min; temp <= tempRange.max; temp += 0.2) {
          const humidityRatio = Math.max(0, (enthalpy - temp) / (2501 + 1.86 * temp));
          const humidityGrams = humidityRatio * 1000;

          // 엔탈피 보조선상의 습도 계산
          const boundaryHumidity = boundaryStartHumidity +
            (boundaryEndHumidity - boundaryStartHumidity) * (temp - tempRange.min) / (boundaryEndTemp - tempRange.min);

          const maxAllowedHumidity = Math.min(boundaryHumidity, humidityRange.max);

          // 엔탈피 선이 실제로 그려지는 조건과 동일
          if (humidityGrams >= humidityRange.min && humidityGrams <= maxAllowedHumidity) {
            const coords = getCanvasCoords(temp, humidityGrams);

            // 보조선과의 거리 계산
            const distance = Math.abs(humidityGrams - boundaryHumidity);

            // 보조선에 더 가까운 점을 찾으면 업데이트
            if (distance < minDistance && prevCoords) {
              minDistance = distance;
              closestPoint = coords;
              beforeClosestPoint = prevCoords;
            }

            prevCoords = coords;
          }
        }

        // 가장 가까운 점과 그 전 점을 찾았으면 라벨 배치
        if (closestPoint && beforeClosestPoint) {
          // 엔탈피 선의 방향 벡터를 반대로 계산 (보조선 교차점 → 차트 바깥쪽)
          const dirX = beforeClosestPoint.x - closestPoint.x;
          const dirY = beforeClosestPoint.y - closestPoint.y;
          const dirLength = Math.sqrt(dirX * dirX + dirY * dirY);
          const unitDirX = dirX / dirLength;  // 안쪽 방향 그대로 (beforeClosest → closest)
          const unitDirY = dirY / dirLength;

          // 가장 가까운 점에서 바깥쪽 방향으로 라벨 이동
          const offsetDistance = 20;
          const labelX = closestPoint.x + unitDirX * offsetDistance;
          const labelY = closestPoint.y + unitDirY * offsetDistance;

          // 텍스트 회전 각도: 엔탈피 선과 동일한 방향
          let enthalpyAngle = Math.atan2(dirY, dirX);

          // 텍스트가 거꾸로 되지 않도록 각도 조정 (읽기 편한 방향 유지)
          // -90도 ~ +90도 범위로 유지
          if (enthalpyAngle > Math.PI / 2) {
            enthalpyAngle -= Math.PI;
          } else if (enthalpyAngle < -Math.PI / 2) {
            enthalpyAngle += Math.PI;
          }

          ctx.fillStyle = '#9c27b0';
          ctx.font = '12px Arial';
          ctx.save();
          ctx.translate(labelX, labelY);
          ctx.rotate(enthalpyAngle);
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${enthalpy}`, 0, 0);
          ctx.restore();
        }
      }
    });

    ctx.restore();
  };

  // 엔탈피 경계선만 그리기 (참조선 표시 시 사용) - 포화상대습도선 기반
  const drawEnthalpyBoundary = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();



    // 엔탈피 보조 경계선: 시작점(최소온도, 포화습도+1) → 끝점(최대온도-1, 최대습도)

    // 시작점: 최소 건구온도에서 상대습도 100%로 구한 절대습도 + 1
    let startHumidity = 0;
    try {
      const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(tempRange.min, 100, elevation);
      startHumidity = satHumidityRatio * 1000 + 1; // +1 g/kg
    } catch (_error) {
      // 근사치 계산
      const saturationPressure = 610.78 * Math.exp(17.27 * tempRange.min / (tempRange.min + 237.3));
      const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
      const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
      startHumidity = satHumidityRatio * 1000 + 1; // +1 g/kg
    }

    // 끝점: 최대 건구온도에서 포화선과 최대 절대습도선의 교차점 온도 - 1
    let endTemp = tempRange.max - 1;
    let endHumidity = humidityRange.max;

    // 포화선이 최대 절대습도와 만나는 온도 찾기
    for (let temp = tempRange.max; temp >= tempRange.min; temp -= 0.5) {
      let satHumidity = 0;
      try {
        const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);
        satHumidity = satHumidityRatio * 1000;
      } catch (_error) {
        const saturationPressure = 610.78 * Math.exp(17.27 * temp / (temp + 237.3));
        const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
        const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
        satHumidity = satHumidityRatio * 1000;
      }

      if (satHumidity <= humidityRange.max) {
        endTemp = temp - 1; // -1°C
        break;
      }
    }


    // 엔탈피 보조 경계선 그리기 (포화상대습도선과 동일한 스타일) - 검정색으로 변경
    ctx.strokeStyle = '#000000'; // 검정색 (포화선과 동일)
    ctx.lineWidth = 2; // 포화선과 동일한 두께
    ctx.setLineDash([]); // 실선 (포화선과 동일)

    const startCoords = getCanvasCoords(tempRange.min, startHumidity);
    const endCoords = getCanvasCoords(endTemp, endHumidity);

    // 대각선 엔탈피 경계선 그리기
    ctx.beginPath();
    ctx.moveTo(startCoords.x, startCoords.y);
    ctx.lineTo(endCoords.x, endCoords.y);
    ctx.stroke();

    // 왼쪽 끝점에서 수직선 (아래로) - 구멍 막기
    const leftBottomCoords = getCanvasCoords(tempRange.min, humidityRange.min);
    ctx.beginPath();
    ctx.moveTo(startCoords.x, startCoords.y);
    ctx.lineTo(leftBottomCoords.x, leftBottomCoords.y);
    ctx.stroke();

    // 오른쪽 끝점에서 수평선 (오른쪽으로) - 구멍 막기
    const rightTopCoords = getCanvasCoords(tempRange.max, endHumidity);
    ctx.beginPath();
    ctx.moveTo(endCoords.x, endCoords.y);
    ctx.lineTo(rightTopCoords.x, rightTopCoords.y);
    ctx.stroke();

    // 엔탈피 라벨만 표시 ('엔탈피 h, kJ/kg dry air')
    // 경계선의 방향 벡터 계산
    const boundaryDx = endCoords.x - startCoords.x;
    const boundaryDy = endCoords.y - startCoords.y;
    const boundaryLength = Math.sqrt(boundaryDx * boundaryDx + boundaryDy * boundaryDy);
    const boundaryUnitX = boundaryDx / boundaryLength;
    const boundaryUnitY = boundaryDy / boundaryLength;

    // 수직 방향 벡터 (시계방향 90도 회전)
    const perpX = -boundaryUnitY;
    const perpY = boundaryUnitX;

    const labelT = 0.5; // 경계선 중간
    const labelX = startCoords.x + boundaryDx * labelT - perpX * 50;
    const labelY = startCoords.y + boundaryDy * labelT - perpY * 50;

    ctx.save();
    ctx.translate(labelX, labelY);
    // 경계선과 평행하게 회전
    const angle = Math.atan2(boundaryDy, boundaryDx);
    ctx.rotate(angle);
    ctx.font = 'bold 12px Arial';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('엔탈피 h, kJ/kg dry air', 0, 0);
    ctx.restore();

    ctx.restore();
  };

  const drawPoints = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, tempRange, humidityRange, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    points.forEach(point => {
      if (!point) return; // null point 체크
      let humidityGrams: number;

      if (point.humidityRatio !== undefined) {
        humidityGrams = point.humidityRatio * 1000;
      } else if (point.relativeHumidity !== undefined) {
        try {
          const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            point.dryBulbTemp,
            point.relativeHumidity,
            elevation
          );
          humidityGrams = humidityRatio * 1000;
        } catch (error) {
          return;
        }
      } else {
        return;
      }

      if (point.dryBulbTemp >= tempRange.min && point.dryBulbTemp <= tempRange.max &&
        humidityGrams >= humidityRange.min && humidityGrams <= humidityRange.max) {
        const { x, y } = getCanvasCoords(point.dryBulbTemp, humidityGrams);

        ctx.fillStyle = point.color;
        ctx.beginPath();
        ctx.arc(x, y, point.size || 6, 0, 2 * Math.PI);
        ctx.fill();

        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();

        if (showLabels) {
          // 라벨 변환 함수 (환기=①, 외기=②)
          const getFormattedLabel = (name: string): string => {
            // 원본 VBA와 일치하도록 간단한 영문 라벨 사용
            // 냉방 상태점
            if (name === 'r1') {
              return 'r1';
            } else if (name.includes('RA') || name.includes('ra')) {
              return 'ra';
            } else if (name.includes('OA') || name.includes('oa')) {
              return 'oa';
            } else if (name.includes('MA') || name.includes('ma')) {
              return 'ma';
            } else if (name.includes('SA') || name.includes('sa') || name.includes('LA') || name.includes('la')) {
              return 'la';
            } else if (name.includes('L1') || name.includes('l1')) {
              return 'l1';
            } else if (name.includes('ADP')) {
              return name.includes('계산불가') ? name : 'ADP';
            }
            // 난방 상태점
            else if (name.includes('r2')) {
              return 'r2';
            } else if (name.includes('o2')) {
              return 'o2';
            } else if (name.includes('m1')) {
              return 'ma';
            } else if (name.includes('l2')) {
              return 'l2';
            } else if (name.includes('l3')) {
              return 'l3';
            } else {
              return name.toLowerCase();
            }
          };

          const formattedName = getFormattedLabel(point.name || 'Unknown');

          // 더 큰 폰트 크기 (20% 증가: 12px -> 14.4px ≈ 15px)
          ctx.font = 'bold 15px Arial';

          // 라벨 위치 조정을 위해 먼저 텍스트 폭 측정
          const textMetrics = ctx.measureText(formattedName);
          const textWidth = textMetrics.width;

          // 라벨 위치 조정 (labelPosition에 따라)
          let labelX = x + 10;
          let labelY = y - 10;

          const labelPosition = point.labelPosition || 'top-right';
          const offset = 8; // 라벨을 상태점에 더 가깝게

          // 특정 상태점들의 라벨 위치 조정
          const pointName = (point.name || '').toLowerCase();
          if (pointName.includes('l1')) {
            // L1은 왼쪽으로 0.5°C 만큼 이동
            const leftwardShift = (0.5 / (tempRange.max - tempRange.min)) * chartWidth;
            labelY = y - offset;
            labelX = x - leftwardShift;
          } else if (pointName.includes('la')) {
            // LA는 아래로 0.5 g/kg (절대습도) 만큼 이동
            const downwardShift = (0.5 / (humidityRange.max - humidityRange.min)) * chartHeight;
            labelY = y + offset + downwardShift;
            labelX = x + offset;
          } else if (pointName.includes('adp')) {
            // ADP는 상태점과 같은 높이, 라벨 오른쪽 끝이 상태점 왼쪽 5px 떨어지도록 배치
            labelY = y;
            labelX = x - offset - textWidth - 5; // 텍스트 폭 + 5px 추가 여백
          } else if (pointName === 'r1') {
            // R1은 오른쪽 위로 배치 (5px 추가 이동)
            labelY = y - offset;
            labelX = x + offset + 5;
          } else if (pointName.includes('ra')) {
            // RA는 아래로 0.5 g/kg (절대습도) 만큼 이동
            const downwardShift = (0.5 / (humidityRange.max - humidityRange.min)) * chartHeight;
            labelY = y + offset + downwardShift;
            labelX = x + offset;
          } else if (pointName.includes('m1')) {
            // ma(m1)는 아래로 1 g/kg (절대습도) 만큼 이동
            const downwardShift = (1 / (humidityRange.max - humidityRange.min)) * chartHeight;
            labelY = y + offset + downwardShift;
            labelX = x + offset;
          } else if (pointName.includes('r2')) {
            // r2는 위로 0.5 g/kg (절대습도) 만큼 이동
            const upwardShift = (0.5 / (humidityRange.max - humidityRange.min)) * chartHeight;
            labelY = y - offset - upwardShift;
            labelX = x + offset;
          } else if (pointName.includes('l3')) {
            // l3는 아래로 0.7 g/kg (절대습도) 만큼 이동
            const downwardShift = (0.7 / (humidityRange.max - humidityRange.min)) * chartHeight;
            labelY = y + offset + downwardShift;
            labelX = x + offset;
          } else {
            switch (labelPosition) {
              case 'top-left':
                labelX = x - offset;
                labelY = y - offset;
                break;
              case 'top-right':
                labelX = x + offset;
                labelY = y - offset;
                break;
              case 'bottom-left':
                labelX = x - offset;
                labelY = y + offset;
                break;
              case 'bottom-right':
                labelX = x + offset;
                labelY = y + offset;
                break;
              case 'center':
                labelX = x;
                labelY = y;
                break;
              default:
                // 기본값: top-right
                labelX = x + offset;
                labelY = y - offset;
                break;
            }
          }

          // 텍스트 높이 (이미 위에서 textWidth는 측정됨)
          const textHeight = 15; // 폰트 크기와 동일

          // 투명 배경 상자 그리기 (패딩 포함)
          const padding = 3;
          const boxX = labelX - padding;
          const boxY = labelY - textHeight - padding;
          const boxWidth = textWidth + (padding * 2);
          const boxHeight = textHeight + (padding * 2);

          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'; // 투명한 흰색 배경
          ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

          // 상자 테두리 그리기 (더 얇게)
          ctx.strokeStyle = 'rgba(200, 200, 200, 0.4)';
          ctx.lineWidth = 0.3;
          ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

          // 라벨 텍스트 그리기
          ctx.fillStyle = '#000000';
          ctx.fillText(formattedName, labelX, labelY);
        }
      }
    });
    ctx.restore();
  };

  // 상태점 정보를 첫 번째 열에 표시하는 함수
  const drawStatePointsInfo = (_ctx: CanvasRenderingContext2D) => {
    // This function is deprecated and no longer used
    // State point info is now displayed using StatePointsInfo.tsx component
    return;
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    ctx.strokeRect(statePointsArea.x, statePointsArea.y, statePointsArea.width, statePointsArea.height);

    // 제목 그리기
    ctx.fillStyle = '#495057';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('상태점 정보', statePointsArea.x + 10, statePointsArea.y + 25);

    // 라벨과 기호 인덱스 변환 함수
    const getFormattedLabelAndSymbol = (name: string): { label: string, symbolIndex: number } => {
      const pointName = (name || '').toLowerCase();
      let symbolIndex = 0;
      let label = name.toLowerCase(); // 소문자로 변경

      if (pointName === 'r1') {
        symbolIndex = 1;
        label = 'r1(환기 온도상승 후)';
      } else if (pointName.includes('ra') || (name.includes('환기') && name.includes('RA'))) {
        symbolIndex = 1;
        label = 'ra(환기)';
      } else if (pointName === 'r2') {
        symbolIndex = 1;
        label = 'r2(난방 환기)';
      } else if (pointName.includes('oa') || (name.includes('외기') && name.includes('OA'))) {
        symbolIndex = 2;
        label = 'oa(외기)';
      } else if (pointName === 'o2') {
        symbolIndex = 2;
        label = 'o2(난방 외기)';
      } else if (pointName.includes('ma') || (name.includes('혼합') && name.includes('MA'))) {
        symbolIndex = 3;
        label = 'ma(혼합)';
      } else if (pointName === 'm1') {
        symbolIndex = 3;
        label = 'm1(난방 혼합)';
      } else if (pointName.includes('l1') || (name.includes('냉각후') && name.includes('L1'))) {
        symbolIndex = 4;
        label = 'l1(냉각후)';
      } else if (pointName === 'l2') {
        symbolIndex = 4;
        label = 'l2(난방 급기)';
      } else if (pointName.includes('la') && !pointName.includes('sa')) {
        symbolIndex = 5;
        label = 'la(Leave Air)';
      } else if (pointName === 'l3') {
        symbolIndex = 5;
        label = 'l3(난방 가열후)';
      } else if (pointName.includes('sa') || (name.includes('급기') && name.includes('SA'))) {
        symbolIndex = 5;
        label = 'sa(급기)';
      } else if (pointName.includes('adp')) {
        symbolIndex = 6;
        label = 'adp';
      }

      return { label, symbolIndex };
    };

    // 상태점 데이터 계산 함수
    const getPointData = (point: PsychrometricPoint) => {
      // 건구온도와 상대습도는 항상 존재해야 하는 필수값 (재계산 불필요)
      const dryBulbTemp = point.dryBulbTemp;
      const relativeHumidity = point.relativeHumidity;

      // 절대습도는 없으면 계산 (상대습도가 있는 경우에만)
      let humidityRatio = point.humidityRatio;
      if (humidityRatio === undefined && relativeHumidity !== undefined) {
        humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
          dryBulbTemp,
          relativeHumidity,
          elevation
        );
      }

      const enthalpy = PsychrometricCalculator.calculateEnthalpy(
        dryBulbTemp,
        humidityRatio || 0
      );

      return {
        temp: dryBulbTemp.toFixed(1),
        humidity: (relativeHumidity || 0).toFixed(1),
        humidityRatio: ((humidityRatio || 0) * 1000).toFixed(1),
        enthalpy: enthalpy.toFixed(2)
      };
    };

    // 상태점 정렬
    const sortedPoints = points
      .filter(point => point !== null && point !== undefined)
      .slice()
      .sort((a, b) => {
        const getPriority = (name: string | null | undefined) => {
          if (!name) return 99;
          const lowerName = name.toLowerCase();
          // 냉방 상태점 (1-7)
          if (lowerName.includes('oa') && !lowerName.includes('o2')) return 1;
          if (lowerName.includes('ra') && !lowerName.includes('r1') && !lowerName.includes('r2')) return 2;
          if (lowerName === 'r1') return 3;
          if (lowerName.includes('ma') && !lowerName.includes('m1')) return 4;
          if (lowerName.includes('l1')) return 5;
          if (lowerName.includes('sa') || (name.includes('급기') && name.includes('SA'))) return 6;
          if (lowerName.includes('adp')) return 7;
          // 난방 상태점 (11-15)
          if (lowerName.includes('o2')) return 11;
          if (lowerName === 'r2') return 12;
          if (lowerName === 'm1') return 13;
          if (lowerName === 'l3') return 14;
          if (lowerName === 'l2') return 15;
          return 99;
        };
        return getPriority(a?.name) - getPriority(b?.name);
      });

    // 냉방과 난방 상태점 분리
    const coolingPoints = sortedPoints.filter(point => {
      const pointName = (point.name || '').toLowerCase();
      return !pointName.includes('r2') && !pointName.includes('o2') &&
        !pointName.includes('m1') && !pointName.includes('l2') && !pointName.includes('l3');
    });

    const heatingPoints = sortedPoints.filter(point => {
      const pointName = (point.name || '').toLowerCase();
      return pointName.includes('r2') || pointName.includes('o2') ||
        pointName.includes('m1') || pointName.includes('l2') || pointName.includes('l3');
    });

    // 행 레이아웃 설정 (냉방 위, 난방 아래) - 반응형 열 개수
    // 상태점 영역 너비에 따라 1열 또는 2열로 조정
    const columnsCount = statePointsArea.width > 250 ? 2 : 1;
    const itemWidth = (statePointsArea.width - 20) / columnsCount;
    const itemHeight = 58;
    const lineSpacing = 16;
    const smallFont = '12px Arial';
    const smallBoldFont = 'bold 12px Arial';

    let currentY = statePointsArea.y + 70;

    // 첫 번째 행: 냉방 상태점 (파란색)
    if (coolingPoints.length > 0) {
      const startX = statePointsArea.x + 10;

      // 냉방 상태점 타이틀
      ctx.fillStyle = '#2196F3'; // 파란색
      ctx.font = 'bold 15px Arial';
      ctx.fillText('■ 냉방 상태점', startX, currentY);

      let rowY = currentY + 25;
      let colIndex = 0;

      coolingPoints.forEach((point, idx) => {
        const data = getPointData(point);
        const { label, symbolIndex } = getFormattedLabelAndSymbol(point.name);

        const posX = startX + (colIndex * itemWidth);

        // 상태점 색상 원
        ctx.fillStyle = point.color;
        ctx.beginPath();
        ctx.arc(posX + 8, rowY + 8, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.stroke();

        // 상태점 라벨(이름)
        ctx.fillStyle = '#000000';
        ctx.font = smallBoldFont;
        ctx.fillText(label, posX + 18, rowY + 12);

        // 세부정보
        let infoText = '';
        if (symbolIndex > 0) {
          infoText = `t${symbolIndex}=${data.temp}℃, ${data.humidity}%, x${symbolIndex}=${data.humidityRatio}`;
        } else {
          infoText = `${data.temp}℃, ${data.humidity}%, ${data.humidityRatio}g/kg`;
        }
        ctx.font = smallFont;
        ctx.fillStyle = '#555555';
        ctx.fillText(infoText, posX + 18, rowY + 12 + lineSpacing);

        // 엔탈피 정보 (있는 경우)
        if (symbolIndex > 0 && data.enthalpy) {
          ctx.fillText(`h${symbolIndex}=${data.enthalpy}kJ/kg`, posX + 18, rowY + 12 + lineSpacing * 2);
        }

        colIndex++;
        if (colIndex >= columnsCount) { // 반응형 열 개수
          colIndex = 0;
          rowY += itemHeight;
        }
      });

      // 다음 섹션 시작 위치 (섹션 간 간격 증가)
      if (colIndex > 0) {
        currentY = rowY + itemHeight + 40;
      } else {
        currentY = rowY + 40;
      }
    }

    // 두 번째 행: 난방 상태점 (빨간색)
    if (heatingPoints.length > 0) {
      const startX = statePointsArea.x + 10;

      // 난방 상태점 타이틀
      ctx.fillStyle = '#F44336'; // 빨간색
      ctx.font = 'bold 15px Arial';
      ctx.fillText('■ 난방 상태점', startX, currentY);

      let rowY = currentY + 25;
      let colIndex = 0;

      heatingPoints.forEach((point, idx) => {
        const data = getPointData(point);
        const { label, symbolIndex } = getFormattedLabelAndSymbol(point.name);

        const posX = startX + (colIndex * itemWidth);

        // 상태점 색상 원
        ctx.fillStyle = point.color;
        ctx.beginPath();
        ctx.arc(posX + 8, rowY + 8, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.stroke();

        // 상태점 라벨(이름)
        ctx.fillStyle = '#000000';
        ctx.font = smallBoldFont;
        ctx.fillText(label, posX + 18, rowY + 12);

        // 세부정보
        let infoText = '';
        if (symbolIndex > 0) {
          infoText = `t${symbolIndex}=${data.temp}℃, ${data.humidity}%, x${symbolIndex}=${data.humidityRatio}`;
        } else {
          infoText = `${data.temp}℃, ${data.humidity}%, ${data.humidityRatio}g/kg`;
        }
        ctx.font = smallFont;
        ctx.fillStyle = '#555555';
        ctx.fillText(infoText, posX + 18, rowY + 12 + lineSpacing);

        // 엔탈피 정보 (있는 경우)
        if (symbolIndex > 0 && data.enthalpy) {
          ctx.fillText(`h${symbolIndex}=${data.enthalpy}kJ/kg`, posX + 18, rowY + 12 + lineSpacing * 2);
        }

        colIndex++;
        if (colIndex >= columnsCount) { // 반응형 열 개수
          colIndex = 0;
          rowY += itemHeight;
        }
      });
    }

    ctx.restore();
  };

  // 시스템 다이어그램을 첫 번째 열 첫 번째 행에 표시하는 함수 (실제 HVAC 심볼 사용)
  const drawSystemDiagram = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { diagramArea } = currentDrawingContext;

    ctx.save();

    // 배경 그리기 (반투명 배경으로 오버레이 효과)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fillRect(diagramArea.x, diagramArea.y, diagramArea.width, diagramArea.height);

    // 테두리 (단순한 선)
    ctx.strokeStyle = '#adb5bd';
    ctx.lineWidth = 1;
    ctx.strokeRect(diagramArea.x, diagramArea.y, diagramArea.width, diagramArea.height);

    // 제목 그리기
    ctx.fillStyle = '#495057';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('시스템 다이어그램', diagramArea.x + 10, diagramArea.y + 25);

    // 기본 위치 설정
    const baseX = diagramArea.x + 30;
    const baseY = diagramArea.y + 70;

    // 상단 외기 라인 - EA 댐퍼를 통과하여 그림 (VBA 방식: 나중에 댐퍼 원이 덮어씀)
    const eaDamperRadius = 8;
    const eaDamperX = baseX + 52;  // 배기 댐퍼 중심 위치
    const lineThickness = 2;

    // 상단 배기량 라벨 (EA: 최소~최대) - 최소: (OA 최소값 - TA), 최대: raFlow
    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`${oaFlowMin - (taFlow !== undefined ? taFlow : eaFlow)}`, baseX + 5, baseY - 15);
    ctx.textAlign = 'right';
    ctx.fillText(`~ ${raFlow}`, baseX + 45, baseY - 5);


    // RF (리턴팬) - raFanType이 'None'이 아닐 때만 표시 (VBA 5567: If Not ahdata.Cells(Re + 1, 63) = "None" Then)
    const rfX = baseX + 120 + 20;  // 오른쪽으로 20px 이동
    const rfY = baseY;
    const rfRadius = 12;

    if (raFanType && raFanType !== 'None') {
      // RF 원 테두리
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(rfX, rfY, rfRadius, 0, Math.PI * 2);
      ctx.stroke();

      // RF 빨간 삼각형 (왼쪽 향하는 화살표)
      ctx.fillStyle = '#ff0000';
      ctx.beginPath();
      ctx.moveTo(rfX - 6, rfY);
      ctx.lineTo(rfX + 4, rfY - 6);
      ctx.lineTo(rfX + 4, rfY + 6);
      ctx.closePath();
      ctx.fill();

      // RF 삼각형 가장자리
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();

      // RF 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('RF', rfX, rfY - 18);
    }

    // RA 라인은 나중에 ROOM에서 연결됨 (raFlow > 0일 때만, VBA 5539: If RAvol > 0 Then)

    // RA 풍량 라벨은 나중에 ROOM 연결 부분에서 표시

    // 하단 외기 라인 - 댐퍼 높이에 맞춰 조정
    const oaBottomY = baseY + 106;  // AHU 좌측 중앙 높이 (baseY + 75 + 31, ahuHeight=62의 절반)
    const oaDamperX = baseX + 52;  // 하단 OA 댐퍼 중심 위치 (상단 댐퍼와 동일)
    const oaDamperRadius = 8;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(baseX, oaBottomY);
    // 선 두께(2px)를 고려하여 댐퍼 원 경계보다 1px 더 왼쪽에서 멈춤
    ctx.lineTo(oaDamperX - oaDamperRadius - lineThickness / 2, oaBottomY);
    ctx.stroke();

    // 하단 외기 풍량 라벨 (OA: Outdoor Air Volume ~ Supply Air Volume)
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`${oaFlowMin}`, baseX + 5, oaBottomY + 12);
    ctx.fillText(`~ ${saFlow}`, baseX + 5, oaBottomY + 22);

    // 하단 외기 댐퍼 심볼 - Y축 미러링된 대각선 (AHU 높이에 맞춤)
    const oaDamperY = oaBottomY;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(oaDamperX, oaDamperY, oaDamperRadius, 0, Math.PI * 2);
    ctx.stroke();
    // 댐퍼 대각선 - 원 경계에서 반지름의 2/3 연장
    const oaDamperExtension = oaDamperRadius * 2 / 3;
    const oaDiagonalOffset = oaDamperRadius / Math.sqrt(2);
    ctx.beginPath();
    ctx.moveTo(oaDamperX - oaDiagonalOffset - oaDamperExtension / Math.sqrt(2), oaDamperY + oaDiagonalOffset + oaDamperExtension / Math.sqrt(2));
    ctx.lineTo(oaDamperX + oaDiagonalOffset + oaDamperExtension / Math.sqrt(2), oaDamperY - oaDiagonalOffset - oaDamperExtension / Math.sqrt(2));
    ctx.stroke();

    // AHU 박스 - 리턴 댐퍼와 조화롭게 배치
    // 공조기 폭을 코일 개수에 맞게 동적 계산
    let baseAhuWidth = 102; // 기본 폭 (120 - 18 = 102)
    // 예열코일
    if (preheatingCoilType !== 'None') baseAhuWidth += 20;
    // 냉각코일 (CC) - isCoolingMode일 때만
    if (isCoolingMode) baseAhuWidth += 20;
    // 가열코일 (HC/AH) - 항상 표시
    baseAhuWidth += 20;
    // 재열코일
    if (reheatingCoilType !== 'None') baseAhuWidth += 20;
    // 가습기 (내부 설치)
    if (humidifierType !== 'Steam(O)' && humidifierType !== 'None') baseAhuWidth += 20;

    const ahuWidth = baseAhuWidth;
    const ahuHeight = 62;  // 56px에서 10% 추가 증가 (56 * 1.1 = 61.6 ≈ 62)
    // 리턴 댐퍼를 먼저 계산해야 하므로 아래에서 정의
    const tempReturnDamperX = (baseX + 120 + baseX + 70) / 2;  // RF와 EA 댐퍼 중점
    // AHU 왼쪽 시작점을 고정 (기존 77 폭 기준의 왼쪽 시작점 유지)
    const fixedAhuLeftX = tempReturnDamperX - 77 / 2 + 35;
    const ahuX = fixedAhuLeftX;  // 왼쪽 시작점 고정
    const ahuY = baseY + 50 + 45 - 20;  // 적당한 간격 (아래쪽으로 45px 이동 후 위로 20px 이동)

    // RF와 EA 댐퍼를 연결하고, 중앙에서 AHU로 연결
    // 리턴 댐퍼를 AHU 상단 연결선 위에 배치
    const ahuTopConnectionX = ahuX + (ahuWidth / ((humidifierType === 'Steam(O)' || humidifierType === 'None') ? 4 : 5) * 0.5) / 2;  // 공조기 시작점과 첫 번째 코일 시작점 사이의 중간
    const returnDamperX = ahuTopConnectionX;  // 리턴 댐퍼를 연결선 위에 배치
    const returnDamperY = (baseY + ahuY) / 2;  // RF에서 AHU로 연결되는 수직선 중앙

    // 리턴 댐퍼 반지름 정의 (먼저 정의해야 아래에서 사용 가능)
    const returnDamperRadius = 8;

    // 상단 외기 라인 - EA 댐퍼 왼쪽 경계까지만 (하단 OA 댐퍼와 동일한 방식)
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(baseX, baseY);  // 왼쪽 시작점
    ctx.lineTo(eaDamperX - eaDamperRadius, baseY);  // EA 댐퍼 왼쪽 원 경계까지
    ctx.stroke();

    // 리턴 댐퍼와 AHU 연결 (raFlow > 0일 때만, VBA 5539-5543: If RAvol > 0 Then)
    if (raFlow && raFlow > 0) {
      // EA 댐퍼 오른쪽에서 리턴댐퍼까지 수평선 + 리턴댐퍼까지 수직선 (하단 OA 댐퍼와 동일한 방식)
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      // 선 두께를 고려하여 댐퍼 원 경계보다 1px 더 오른쪽에서 시작
      ctx.moveTo(eaDamperX + eaDamperRadius + lineThickness / 2, baseY);
      ctx.lineTo(returnDamperX, baseY);  // 리턴댐퍼까지 수평
      ctx.lineTo(returnDamperX, returnDamperY - returnDamperRadius);  // 리턴 댐퍼 위쪽 원 경계까지 수직
      ctx.stroke();

      // 리턴 댐퍼에서 AHU까지 (RF 있든 없든 항상 표시)
      ctx.beginPath();
      ctx.moveTo(returnDamperX, returnDamperY + returnDamperRadius);  // 리턴 댐퍼 아래쪽 원 경계에서
      ctx.lineTo(returnDamperX, ahuY);  // AHU 상단까지 수직
      ctx.stroke();

      // 리턴 댐퍼 심볼 (RF 있든 없든 항상 표시)
      ctx.beginPath();
      ctx.arc(returnDamperX, returnDamperY, returnDamperRadius, 0, Math.PI * 2);
      ctx.stroke();
      // 댐퍼 대각선 - 원 경계에서 반지름의 2/3 연장
      const returnDamperExtension = returnDamperRadius * 2 / 3;
      const returnDiagonalOffset = returnDamperRadius / Math.sqrt(2);
      ctx.beginPath();
      ctx.moveTo(returnDamperX - returnDiagonalOffset - returnDamperExtension / Math.sqrt(2), returnDamperY + returnDiagonalOffset + returnDamperExtension / Math.sqrt(2));
      ctx.lineTo(returnDamperX + returnDiagonalOffset + returnDamperExtension / Math.sqrt(2), returnDamperY - returnDiagonalOffset - returnDamperExtension / Math.sqrt(2));
      ctx.stroke();
    }

    // AHU 박스 그리기
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.strokeRect(ahuX, ahuY, ahuWidth, ahuHeight);

    // AHU 내부 구성요소들 (예열코일, 냉각코일, 가열코일, 재열코일, 가습기)
    // Steam(O) 가습기이거나 None일 때는 가습기 섹션 제거하여 섹션 수 조정
    // 예열/재열 코일 존재 여부에 따라 섹션 수 계산
    let activeSections = (humidifierType === 'Steam(O)' || humidifierType === 'None') ? 2 : 3; // 기본: CC, HC, (HF)
    if (preheatingCoilType !== 'None') activeSections++; // PH 추가
    if (reheatingCoilType !== 'None') activeSections++; // RH 추가

    // 코일 간격과 폭 계산 (고정 크기)
    const coilSpacing = 8; // 코일 간 간격
    const leftMargin = 10; // 왼쪽 여백
    const fixedCoilWidth = 18; // 코일 고정 폭
    const coilWidth = fixedCoilWidth;

    let currentX = ahuX + leftMargin; // 시작 X 위치
    const coilY = ahuY + 5;
    const coilHeight = ahuHeight - 10;

    // 1. 예열코일 (PH) - preheatingCoilType이 'None'이 아닐 때만 표시
    if (preheatingCoilType !== 'None') {
      const phWidth = coilWidth * 0.7; // PH는 70% 폭
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(currentX, coilY, phWidth, coilHeight);
      // 대각선 그리기 (가열코일 스타일)
      ctx.beginPath();
      ctx.moveTo(currentX, coilY + coilHeight);
      ctx.lineTo(currentX + phWidth, coilY);
      ctx.stroke();

      // PH 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('PH', currentX + phWidth / 2, coilY + coilHeight + 15);

      currentX += phWidth + coilSpacing;
    }

    // 2. 냉각/가열 코일 - VBA 5467: If Not ahdata.Cells(Re + 1, 116) = "ON" And Not ahdata.Cells(Re + 1, 23) = "None"
    // 냉각코일 (CC) - isCoolingMode가 true일 때만 표시
    if (isCoolingMode) {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(currentX, coilY, coilWidth, coilHeight);
      // X자 그리기
      ctx.beginPath();
      ctx.moveTo(currentX, coilY);
      ctx.lineTo(currentX + coilWidth, coilY + coilHeight);
      ctx.moveTo(currentX + coilWidth, coilY);
      ctx.lineTo(currentX, coilY + coilHeight);
      ctx.stroke();

      // CC 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('CC', currentX + coilWidth / 2, coilY + coilHeight + 15);

      currentX += coilWidth + coilSpacing;
    }

    // 3. 가열코일 (HC/AH) - 항상 표시
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(currentX, coilY, coilWidth, coilHeight);
    // 대각선 그리기
    ctx.beginPath();
    ctx.moveTo(currentX, coilY + coilHeight);
    ctx.lineTo(currentX + coilWidth, coilY);
    ctx.stroke();

    // HC 라벨
    ctx.fillStyle = '#000';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('AH', currentX + coilWidth / 2, coilY + coilHeight + 15);

    currentX += coilWidth + coilSpacing;

    // 4. 재열코일 (RH) - reheatingCoilType이 'None'이 아닐 때만 표시
    if (reheatingCoilType !== 'None') {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(currentX, coilY, coilWidth, coilHeight);
      // 대각선 그리기 (가열코일 스타일)
      ctx.beginPath();
      ctx.moveTo(currentX, coilY + coilHeight);
      ctx.lineTo(currentX + coilWidth, coilY);
      ctx.stroke();

      // RH 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('RH', currentX + coilWidth / 2, coilY + coilHeight + 15);

      currentX += coilWidth + coilSpacing;
    }

    // 5. 가습기 (HF) - Steam(O)가 아니고 None도 아닌 경우에만 AHU 내부에 그리기
    if (humidifierType !== 'Steam(O)' && humidifierType !== 'None') {
      // 가습기 세로선
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(currentX + coilWidth / 2, coilY);
      ctx.lineTo(currentX + coilWidth / 2, coilY + coilHeight);
      ctx.stroke();

      // 가습기 V자 모양들 (수직선 기준 왼쪽을 향하는 V자 3개)
      ctx.lineWidth = 1;
      const verticalLineX = currentX + coilWidth / 2;  // 수직선 X 위치

      // 수직선 양끝점 10% 지점과 중앙에 V자 배치
      const vPositions = [
        coilY + coilHeight * 0.1,   // 위쪽 10% 지점
        coilY + coilHeight * 0.5,   // 중앙
        coilY + coilHeight * 0.9    // 아래쪽 10% 지점
      ];

      for (let i = 0; i < vPositions.length; i++) {
        const vY = vPositions[i];

        // V자를 수직선 왼쪽으로 더 이동 (> 모양을 왼쪽에 배치)
        ctx.beginPath();
        ctx.moveTo(verticalLineX - 5, vY - 3);  // 위쪽 끝 (더 왼쪽으로 이동)
        ctx.lineTo(verticalLineX - 2, vY);      // 꼭짓점 (왼쪽으로 이동)
        ctx.lineTo(verticalLineX - 5, vY + 3);  // 아래쪽 끝 (더 왼쪽으로 이동)
        ctx.stroke();
      }

      // HF 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('HF', currentX + coilWidth / 2, coilY + coilHeight + 15);

      currentX += coilWidth + coilSpacing;
    }

    // SF (송풍팬) - sf.png 스타일: 원 안에 보라색 삼각형
    // 공조기 오른쪽 벽에서 적절한 여백 확보
    const sfRightMargin = 18; // SF와 공조기 오른쪽 벽 사이 여백
    const sfX = ahuX + ahuWidth - sfRightMargin;
    const sfY = ahuY + ahuHeight / 2;
    const sfRadius = 12;

    // SF 원 테두리
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(sfX, sfY, sfRadius, 0, Math.PI * 2);
    ctx.stroke();

    // SF 보라색 삼각형 (오른쪽 향하는 화살표)
    ctx.fillStyle = '#320080';  // 배경색을 #320080으로 변경
    ctx.beginPath();
    ctx.moveTo(sfX + 6, sfY);
    ctx.lineTo(sfX - 4, sfY - 6);
    ctx.lineTo(sfX - 4, sfY + 6);
    ctx.closePath();
    ctx.fill();

    // SF 삼각형 가장자리
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();

    // SF 라벨
    ctx.fillStyle = '#000';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('SF', sfX, sfY + 25);  // SF 기호와 겹치지 않도록 더 아래로 이동

    // 급기덕트 아이콘 (RA Lighting Load가 'separate'일 때만 표시)
    if (raLightingLoadMode === 'separate') {
      // SF 위쪽에 급기덕트 단면 표시
      const ductX = sfX;
      const ductY = sfY - 30; // SF 위 30px
      const ductWidth = 24;
      const ductHeight = 12;

      // 외부 사각형 (덕트 외곽)
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(ductX - ductWidth / 2, ductY - ductHeight / 2, ductWidth, ductHeight);

      // 내부 보라색 삼각형 (기류 방향)
      ctx.fillStyle = '#320080';
      ctx.beginPath();
      ctx.moveTo(ductX + 6, ductY); // 오른쪽 꼭지점
      ctx.lineTo(ductX - 3, ductY - 5); // 왼쪽 위
      ctx.lineTo(ductX - 3, ductY + 5); // 왼쪽 아래
      ctx.closePath();
      ctx.fill();

      // 삼각형 테두리
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // 상단 외기는 EA 댐퍼에 연결됨 (RF-EA 댐퍼 연결선이 이미 처리함)

    // 하단 외기 댐퍼에서 AHU 좌측 중앙으로 연결 (수평 연결, 선 두께 고려)
    ctx.strokeStyle = '#000';  // 검정색으로 명시적 설정
    ctx.lineWidth = 2;
    ctx.beginPath();
    // 선 두께를 고려하여 댐퍼 원 경계보다 1px 더 오른쪽에서 시작
    ctx.moveTo(oaDamperX + oaDamperRadius + lineThickness / 2, oaBottomY);
    ctx.lineTo(ahuX, oaBottomY);  // AHU 좌측까지 수평 연결
    ctx.stroke();

    // SA 라인 (AHU에서 ROOM으로) - 50px 아래로 이동
    const roomX = ahuX + ahuWidth + 60;  // 공조기 끝에서 60px 오른쪽
    const roomY = baseY + 75;  // 25 + 50 = 75
    const roomWidth = ahuHeight;  // 공조기 높이와 같은 크기로 정사각형
    const roomHeight = ahuHeight;  // 공조기 높이와 같은 크기로 정사각형

    ctx.beginPath();
    ctx.moveTo(ahuX + ahuWidth, sfY);  // 공조기 오른쪽 벽에서 시작
    ctx.lineTo(roomX, sfY);
    ctx.stroke();

    // SA 풍량 라벨 (공조기 벽과 ROOM 사이 선의 중앙으로 이동)
    const saLabelX = (ahuX + ahuWidth + roomX) / 2;  // 공조기 벽과 ROOM 왼쪽 사이의 중점
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('SA', saLabelX, sfY - 8);
    ctx.fillText(`${saFlow}`, saLabelX, sfY - 18);

    // ROOM 박스
    ctx.strokeRect(roomX, roomY, roomWidth, roomHeight);

    // ROOM 라벨
    ctx.fillStyle = '#000';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('ROOM', roomX + roomWidth / 2, roomY + roomHeight / 2 + 4);

    // TA (Total Air) - 점선 화살표 (길게 연장)
    const eaX = roomX + roomWidth;
    const eaY = roomY + roomHeight / 2;

    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(eaX, eaY);
    ctx.lineTo(eaX + 60, eaY);  // 40에서 60으로 50% 연장
    ctx.stroke();
    ctx.setLineDash([]);

    // TA 화살표 (길어진 선에 맞춰 위치 조정)
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.moveTo(eaX + 48, eaY - 3.6);  // 길어진 선(60px)에 맞춰 화살표 위치 조정
    ctx.lineTo(eaX + 60, eaY);
    ctx.lineTo(eaX + 48, eaY + 3.6);
    ctx.closePath();
    ctx.fill();

    // TA 라벨 (EA에서 TA로 변경)
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('TA', eaX + 20, eaY - 8);  // 라벨 위치도 조정
    ctx.fillText(`${taFlow !== undefined ? taFlow : eaFlow}`, eaX + 20, eaY + 15);  // TA = EA + 가압량

    // ROOM에서 배기/환기 덕트 연결 (항상 표시, VBA 5539-5564)
    const roomCenterX = roomX + roomWidth / 2;  // ROOM 상단 중앙

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(roomCenterX, roomY);  // ROOM 상단 중앙에서 시작

    if (raFanType && raFanType !== 'None') {
      // RF가 있는 경우: ROOM → RF → 배기 댐퍼
      ctx.lineTo(roomCenterX, rfY);  // RF 높이까지 위로
      ctx.lineTo(rfX + rfRadius, rfY);  // RF까지 가로
      ctx.stroke();

      // RA 풍량 라벨 (raFlow > 0일 때만, RF 오른쪽 선 중앙)
      if (raFlow && raFlow > 0) {
        const raLabelX = (rfX + rfRadius + roomCenterX) / 2;
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`RA     ${raFlow}`, raLabelX, rfY - 8);
      }
    } else {
      // RF가 없는 경우: ROOM → EA 댐퍼 왼쪽 경계까지 (하단 OA 댐퍼와 동일한 방식)
      ctx.lineTo(roomCenterX, baseY);  // baseY 높이까지 위로
      ctx.lineTo(eaDamperX - eaDamperRadius, baseY);  // EA 댐퍼 왼쪽 원 경계까지
      ctx.stroke();

      // RA 풍량 라벨 (raFlow > 0일 때만, EA 댐퍼 왼쪽)
      if (raFlow && raFlow > 0) {
        const raLabelX = (eaDamperX - eaDamperRadius + roomCenterX) / 2;
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`RA     ${raFlow}`, raLabelX, baseY - 8);
      }
    }

    // EA 댐퍼 심볼 (덕트 라인 위에 덮어씌움, VBA 5555-5556)
    const eaDamperY = baseY;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(eaDamperX, eaDamperY, eaDamperRadius, 0, Math.PI * 2);
    ctx.stroke();
    // 댐퍼 대각선 (Y축 미러링: 왼쪽 위에서 오른쪽 아래로) - 원 경계에서 반지름의 2/3 연장
    const eaDamperExtension = eaDamperRadius * 2 / 3;
    const eaDiagonalOffset = eaDamperRadius / Math.sqrt(2); // 45도 대각선의 원 경계점
    ctx.beginPath();
    ctx.moveTo(eaDamperX - eaDiagonalOffset - eaDamperExtension / Math.sqrt(2), eaDamperY + eaDiagonalOffset + eaDamperExtension / Math.sqrt(2));
    ctx.lineTo(eaDamperX + eaDiagonalOffset + eaDamperExtension / Math.sqrt(2), eaDamperY - eaDiagonalOffset - eaDamperExtension / Math.sqrt(2));
    ctx.stroke();

    // Steam(O) 가습기를 AHU 바깥 오른편에 그리기
    if (humidifierType === 'Steam(O)') {
      const hdX = ahuX + ahuWidth + 5;  // AHU 바로 옆에 위치 (화살표 끝 위치)
      const hdY = ahuY + ahuHeight / 2;  // AHU 중앙 높이
      const hdWidth = 25;
      const hdHeight = 35;

      // 가습기 박스 제거 (상자 없이 내부 구성요소만 표시)

      // 가습기 내부 세로선
      ctx.beginPath();
      ctx.moveTo(hdX + hdWidth / 2, hdY - hdHeight / 2 + 3);
      ctx.lineTo(hdX + hdWidth / 2, hdY + hdHeight / 2 - 3);
      ctx.stroke();

      // 가습기 V자 모양들 (수직선 기준 왼쪽을 향하는 V자 3개)
      ctx.lineWidth = 1;
      const hdVerticalLineX = hdX + hdWidth / 2;
      const hdVPositions = [
        hdY - hdHeight * 0.25,   // 위쪽
        hdY,                     // 중앙
        hdY + hdHeight * 0.25    // 아래쪽
      ];

      for (let i = 0; i < hdVPositions.length; i++) {
        const vY = hdVPositions[i];
        ctx.beginPath();
        ctx.moveTo(hdVerticalLineX - 4, vY - 2);
        ctx.lineTo(hdVerticalLineX - 1, vY);
        ctx.lineTo(hdVerticalLineX - 4, vY + 2);
        ctx.stroke();
      }

      // HD 라벨
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('HD', hdX + hdWidth / 2, hdY + hdHeight / 2 + 15);

      // 연결선과 화살표 제거 (가습기가 AHU 바로 옆에 위치)
    }

    ctx.restore();
  };

  const drawProcessLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, getCanvasCoords } = currentDrawingContext;

    ctx.save();

    // 차트 영역으로 클리핑 설정 (프로세스 라인이 차트 밖으로 나가지 않도록)
    ctx.beginPath();
    ctx.rect(0, 0, chartWidth, chartHeight);
    ctx.clip();

    // 디버그: 받은 points와 lines 확인
    lines.forEach(line => {
      // 유연한 이름 매칭으로 점을 찾기
      const fromPoint = points.find(p => p && p.name && (
        p.name === line.from ||
        p.name.toLowerCase() === line.from.toLowerCase() ||
        p.name.includes(line.from) ||
        line.from.includes(p.name.split('(')[0]) ||
        p.name.split('(')[0].toLowerCase() === line.from.toLowerCase() ||
        p.name.toLowerCase().trim() === line.from.toLowerCase().trim()
      ));
      const toPoint = points.find(p => p && p.name && (
        p.name === line.to ||
        p.name.toLowerCase() === line.to.toLowerCase() ||
        p.name.includes(line.to) ||
        line.to.includes(p.name.split('(')[0]) ||
        p.name.split('(')[0].toLowerCase() === line.to.toLowerCase() ||
        p.name.toLowerCase().trim() === line.to.toLowerCase().trim()
      ));

      if (!fromPoint || !toPoint) {
        return;
      }

      // 습도비 계산
      let fromHumidity: number;
      let toHumidity: number;

      try {
        fromHumidity = fromPoint.humidityRatio !== undefined ?
          fromPoint.humidityRatio * 1000 :
          PsychrometricCalculator.calculateHumidityRatio(
            fromPoint.dryBulbTemp,
            fromPoint.relativeHumidity || 50,
            elevation
          ) * 1000;

        toHumidity = toPoint.humidityRatio !== undefined ?
          toPoint.humidityRatio * 1000 :
          PsychrometricCalculator.calculateHumidityRatio(
            toPoint.dryBulbTemp,
            toPoint.relativeHumidity || 50,
            elevation
          ) * 1000;
      } catch (error) {
        return;
      }

      const fromCoords = getCanvasCoords(fromPoint.dryBulbTemp, fromHumidity);
      const toCoords = getCanvasCoords(toPoint.dryBulbTemp, toHumidity);

      // 상태점 반지름 + 선 두께 고려
      const pointRadius = 6;
      const lineThickness = (line.width || 2) + 1;
      const totalOffset = pointRadius + (lineThickness / 2) + 1; // 상태점 반지름 + 선 두께 반 + 여유 공간

      // 선의 각도 계산
      const lineAngle = Math.atan2(toCoords.y - fromCoords.y, toCoords.x - fromCoords.x);

      // 상태점 바깥쪽으로 더 멀리 떨어진 좌표 계산
      const adjustedFromCoords = {
        x: fromCoords.x + totalOffset * Math.cos(lineAngle),
        y: fromCoords.y + totalOffset * Math.sin(lineAngle)
      };

      const adjustedToCoords = {
        x: toCoords.x - totalOffset * Math.cos(lineAngle),
        y: toCoords.y - totalOffset * Math.sin(lineAngle)
      };

      // 선의 실제 길이 계산 (상태점 간격을 제외한 순수 선의 길이)
      const lineLength = Math.sqrt(
        Math.pow(adjustedToCoords.x - adjustedFromCoords.x, 2) +
        Math.pow(adjustedToCoords.y - adjustedFromCoords.y, 2)
      );

      // l1->la 선이 너무 짧은 경우 확인 (30px 미만이면 짧다고 판단)
      const isShortLine = (line.from.toLowerCase().includes('l1') && line.to.toLowerCase().includes('la')) && lineLength < 30;

      // 선 그리기 - 프로세스 타입에 따른 스타일과 색상
      ctx.beginPath();

      // 모든 프로세스 라인을 솔리드(실선)로 통일
      ctx.setLineDash([]); // 실선
      ctx.strokeStyle = line.color;
      ctx.lineWidth = line.width || 2;


      ctx.moveTo(adjustedFromCoords.x, adjustedFromCoords.y);
      ctx.lineTo(adjustedToCoords.x, adjustedToCoords.y);
      ctx.stroke();

      // 화살표 제거 (모든 선에서 화살표 없이 그리기)

      // 라벨 그리기 (바이패스 라인은 라벨 숨김)
      if (line.label && showLabels && line.type !== 'bypass') {
        const midX = (fromCoords.x + toCoords.x) / 2;
        const midY = (fromCoords.y + toCoords.y) / 2;

        ctx.fillStyle = line.color;
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(line.label, midX, midY - 5);
      }
    });

    ctx.restore();
  };

  // 각 상태점에서 온도, 절대습도 참조선 그리기
  const drawReferenceLines = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    ctx.strokeStyle = '#ff0000'; // 빨간색 참조선
    ctx.lineWidth = 1; // 얇게 조정
    ctx.setLineDash([3, 3]); // 점선을 더 섬세하게



    points.forEach((point) => {
      if (!point) return; // null point 체크
      let humidityGrams: number;

      if (point.humidityRatio !== undefined) {
        humidityGrams = point.humidityRatio * 1000;
      } else if (point.relativeHumidity !== undefined) {
        try {
          const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            point.dryBulbTemp,
            point.relativeHumidity,
            elevation
          );
          humidityGrams = humidityRatio * 1000;
        } catch (error) {
          return;
        }
      } else {
        return;
      }

      if (point.dryBulbTemp >= tempRange.min && point.dryBulbTemp <= tempRange.max &&
        humidityGrams >= humidityRange.min && humidityGrams <= humidityRange.max) {

        const { x, y } = getCanvasCoords(point.dryBulbTemp, humidityGrams);

        // 수직선 (온도선) - 아래축까지
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, chartHeight);
        ctx.stroke();

        // 수평선 (절대습도선) - 오른쪽 축까지
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(chartWidth, y);
        ctx.stroke();

        // 엔탈피 참조선 - 상태점에서 엔탈피 선과 엔탈피 경계선의 교차점 연결
        // 엔탈피 계산: h = t + x*(2501 + 1.86*t)
        const currentEnthalpy = point.dryBulbTemp + (humidityGrams / 1000) * (2501 + 1.86 * point.dryBulbTemp);

        // 엔탈피 보조 경계선 정보 (동일한 로직으로 계산)
        let boundaryStartHumidity = 0;
        try {
          const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(tempRange.min, 100, elevation);
          boundaryStartHumidity = satHumidityRatio * 1000 + 1;
        } catch (_error) {
          const saturationPressure = 610.78 * Math.exp(17.27 * tempRange.min / (tempRange.min + 237.3));
          const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
          const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
          boundaryStartHumidity = satHumidityRatio * 1000 + 1;
        }

        let boundaryEndTemp = tempRange.max - 1;
        for (let temp = tempRange.max; temp >= tempRange.min; temp -= 0.5) {
          let satHumidity = 0;
          try {
            const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);
            satHumidity = satHumidityRatio * 1000;
          } catch (_error) {
            const saturationPressure = 610.78 * Math.exp(17.27 * temp / (temp + 237.3));
            const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
            const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
            satHumidity = satHumidityRatio * 1000;
          }
          if (satHumidity <= humidityRange.max) {
            boundaryEndTemp = temp - 1;
            break;
          }
        }

        // 경계선: 직선 방정식 y = mx + b
        const boundarySlope = (humidityRange.max - boundaryStartHumidity) / (boundaryEndTemp - tempRange.min);
        const boundaryIntercept = boundaryStartHumidity - boundarySlope * tempRange.min;

        // 상태점의 엔탈피 값을 가진 엔탈피 선과 경계선의 교차점 찾기
        // 엔탈피 선: x = (h - t) / (2501 + 1.86*t)
        // 경계선: x = slope * t + intercept
        // 교차점에서 두 식이 같아야 함

        let intersectionTemp = 0;
        let intersectionHumidity = 0;
        let foundIntersection = false;

        // 온도 범위를 스캔하면서 교차점 찾기
        for (let tempTest = tempRange.min; tempTest <= tempRange.max; tempTest += 0.1) {
          // 해당 온도에서 엔탈피 선 상의 습도
          const enthalpyHumidityRatio = Math.max(0, (currentEnthalpy - tempTest) / (2501 + 1.86 * tempTest));
          const enthalpyHumidity = enthalpyHumidityRatio * 1000;

          // 해당 온도에서 경계선 상의 습도
          const boundaryHumidity = boundarySlope * tempTest + boundaryIntercept;

          // 교차점 체크 (두 값이 거의 같은 지점)
          if (Math.abs(enthalpyHumidity - boundaryHumidity) < 0.1 &&
            tempTest >= tempRange.min && tempTest <= boundaryEndTemp &&
            enthalpyHumidity >= humidityRange.min && enthalpyHumidity <= humidityRange.max) {
            intersectionTemp = tempTest;
            intersectionHumidity = enthalpyHumidity;
            foundIntersection = true;
            break;
          }
        }

        if (foundIntersection) {
          // 엔탈피 참조선 그리기: 상태점 → 교차점
          ctx.beginPath();
          ctx.moveTo(x, y);

          const { x: intersectionX, y: intersectionY } = getCanvasCoords(intersectionTemp, intersectionHumidity);
          ctx.lineTo(intersectionX, intersectionY);
          ctx.stroke();
        } else {
        }
      } else {
        // console.warn(`Point ${point.name || 'Unknown'} is out of range`);
      }
    });

    ctx.restore();
  };

  // 축에 값 기호 표시 (상태점 유형에 따른 고정 번호)
  const drawValueSymbols = (ctx: CanvasRenderingContext2D) => {
    if (!currentDrawingContext) return;
    const { chartWidth, chartHeight, getCanvasCoords } = currentDrawingContext;

    ctx.save();
    ctx.fillStyle = '#ff0000';
    ctx.font = '15px Arial'; // 11px → 15px
    ctx.textAlign = 'center';

    // 겹침 방지를 위한 위치 추적
    const usedXPositions: { x: number; y: number; symbol: string }[] = [];
    const usedYPositions: { x: number; y: number; symbol: string }[] = [];
    const usedEnthalpyPositions: { x: number; y: number; symbol: string }[] = [];

    points.forEach(point => {
      if (!point) return;

      let humidityGrams: number;

      if (point.humidityRatio !== undefined) {
        humidityGrams = point.humidityRatio * 1000;
      } else if (point.relativeHumidity !== undefined) {
        try {
          const humidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            point.dryBulbTemp,
            point.relativeHumidity,
            elevation
          );
          humidityGrams = humidityRatio * 1000;
        } catch (_error) {
          return;
        }
      } else {
        return;
      }

      if (point.dryBulbTemp >= tempRange.min && point.dryBulbTemp <= tempRange.max &&
        humidityGrams >= humidityRange.min && humidityGrams <= humidityRange.max) {

        const { x, y } = getCanvasCoords(point.dryBulbTemp, humidityGrams);

        // 상태점 유형에 따른 고정 번호 결정 (소문자 코드 기반)
        let symbolIndex = 0;
        const pointName = (point.name || '').toLowerCase();

        // r1, r2를 먼저 체크 (ra보다 먼저 확인해야 함)
        if (pointName.includes('r1')) {
          symbolIndex = 1; // R1 (냉방용 환기 온도상승 후) = 1번
        } else if (pointName.includes('r2')) {
          symbolIndex = 1; // R2 (난방용 환기) = 1번
        } else if (pointName.includes('ra') || ((point.name || '').includes('환기') && (point.name || '').includes('RA'))) {
          symbolIndex = 1; // 환기(RA) = 1번
        } else if (pointName.includes('oa') || ((point.name || '').includes('외기') && (point.name || '').includes('OA'))) {
          symbolIndex = 2; // 외기(OA) = 2번
        } else if (pointName.includes('o2')) {
          symbolIndex = 2; // O2 (난방용 외기) = 2번
        } else if (pointName.includes('ma') || ((point.name || '').includes('혼합') && (point.name || '').includes('MA'))) {
          symbolIndex = 3; // 혼합공기(MA) = 3번
        } else if (pointName.includes('l1')) {
          symbolIndex = 4; // L1 = 4번
        } else if (pointName.includes('l2')) {
          symbolIndex = 4; // L2 (난방용) = 4번
        } else if (pointName.includes('l3')) {
          symbolIndex = 5; // L3 (난방용) = 5번
        } else if (pointName.includes('la') || pointName.includes('sa') || ((point.name || '').includes('급기') && (point.name || '').includes('SA'))) {
          symbolIndex = 5; // LA/SA = 5번
        } else if (pointName.includes('adp')) {
          symbolIndex = 6; // ADP = 6번
        }

        if (symbolIndex > 0) {
          // X축(온도)에 기호 표시 - 겹침 검사 및 위치 조정
          const tempSymbol = `t${symbolIndex}`;
          let tempX = x;
          let tempY = chartHeight + 40;

          // X축에서 겹침 검사 (20px 이내 겹침 검사)
          const nearbyXSymbols = usedXPositions.filter(pos => Math.abs(pos.x - tempX) < 20);
          if (nearbyXSymbols.length > 0) {
            // 겹침이 있으면 아래쪽으로 추가 이동
            tempY += nearbyXSymbols.length * 20;
          }

          ctx.fillText(tempSymbol, tempX, tempY);
          usedXPositions.push({ x: tempX, y: tempY, symbol: tempSymbol });

          // Y축(절대습도)에 기호 표시 - 겹침 검사 및 위치 조정
          const humiditySymbol = `x${symbolIndex}`;
          let humidityX = chartWidth + 35;
          let humidityY = y + 4;

          // Y축에서 겹침 검사 (15px 이내 겹침 검사)
          const nearbyYSymbols = usedYPositions.filter(pos => Math.abs(pos.y - humidityY) < 15);
          if (nearbyYSymbols.length > 0) {
            // 겹침이 있으면 오른쪽으로 추가 이동
            humidityX += nearbyYSymbols.length * 25;
          }

          ctx.save();
          ctx.textAlign = 'left';
          ctx.fillText(humiditySymbol, humidityX, humidityY);
          ctx.restore();
          usedYPositions.push({ x: humidityX, y: humidityY, symbol: humiditySymbol });

          // 엔탈피 기호 표시 - 엔탈피 선과 엔탈피 경계선의 교차점에서 왼쪽으로 약간 떨어진 곳
          const enthalpySymbol = `h${symbolIndex}`;

          // 엔탈피 교차점 계산 (drawReferenceLines와 동일한 로직)
          const currentEnthalpy = point.dryBulbTemp + (humidityGrams / 1000) * (2501 + 1.86 * point.dryBulbTemp);

          // 엔탈피 보조 경계선 정보 계산
          let boundaryStartHumidity = 0;
          try {
            const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(tempRange.min, 100, elevation);
            boundaryStartHumidity = satHumidityRatio * 1000 + 1;
          } catch (_error) {
            const saturationPressure = 610.78 * Math.exp(17.27 * tempRange.min / (tempRange.min + 237.3));
            const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
            const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
            boundaryStartHumidity = satHumidityRatio * 1000 + 1;
          }

          let boundaryEndTemp = tempRange.max - 1;
          for (let temp = tempRange.max; temp >= tempRange.min; temp -= 0.5) {
            let satHumidity = 0;
            try {
              const satHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);
              satHumidity = satHumidityRatio * 1000;
            } catch (_error) {
              const saturationPressure = 610.78 * Math.exp(17.27 * temp / (temp + 237.3));
              const atmosphericPressure = 101325 * Math.pow(1 - 0.0065 * elevation / 288.15, 5.2561);
              const satHumidityRatio = 0.622 * saturationPressure / (atmosphericPressure - saturationPressure);
              satHumidity = satHumidityRatio * 1000;
            }
            if (satHumidity <= humidityRange.max) {
              boundaryEndTemp = temp - 1;
              break;
            }
          }

          const boundarySlope = (humidityRange.max - boundaryStartHumidity) / (boundaryEndTemp - tempRange.min);
          const boundaryIntercept = boundaryStartHumidity - boundarySlope * tempRange.min;

          // 교차점 찾기
          let intersectionFound = false;
          let intersectionX = 0;
          let intersectionY = 0;

          for (let tempTest = tempRange.min; tempTest <= tempRange.max; tempTest += 0.1) {
            const enthalpyHumidityRatio = Math.max(0, (currentEnthalpy - tempTest) / (2501 + 1.86 * tempTest));
            const enthalpyHumidity = enthalpyHumidityRatio * 1000;
            const boundaryHumidity = boundarySlope * tempTest + boundaryIntercept;

            if (Math.abs(enthalpyHumidity - boundaryHumidity) < 0.1 &&
              tempTest >= tempRange.min && tempTest <= boundaryEndTemp &&
              enthalpyHumidity >= humidityRange.min && enthalpyHumidity <= humidityRange.max) {
              const intersectionCoords = getCanvasCoords(tempTest, enthalpyHumidity);
              intersectionX = intersectionCoords.x;
              intersectionY = intersectionCoords.y;
              intersectionFound = true;
              break;
            }
          }

          if (intersectionFound) {
            // 엔탈피 기호 겹침 검사 및 위치 조정
            let enthalpyX = intersectionX - 8;
            let enthalpyY = intersectionY - 8;

            // 25px 이내의 겹침 검사 (엔탈피 기호는 더 큰 이격 거리 필요)
            const nearbyEnthalpySymbols = usedEnthalpyPositions.filter(pos =>
              Math.abs(pos.x - enthalpyX) < 25 && Math.abs(pos.y - enthalpyY) < 25
            );

            if (nearbyEnthalpySymbols.length > 0) {
              // 겹침이 있으면 대각선 방향으로 더 멀리 이동
              const offset = nearbyEnthalpySymbols.length * 30; // 30px씩 이격
              enthalpyX -= offset; // 왼쪽으로 더 이동
              enthalpyY -= offset; // 위쪽으로 더 이동
            }

            ctx.save();
            ctx.textAlign = 'right';
            ctx.fillText(enthalpySymbol, enthalpyX, enthalpyY);
            ctx.restore();
            usedEnthalpyPositions.push({ x: enthalpyX, y: enthalpyY, symbol: enthalpySymbol });
          }
        }
      }
    });

    ctx.restore();
  };

  useEffect(() => {
    // 표시용 Canvas 렌더링 (반응형 크기)
    drawChart();

    // 차트 그리기가 완료된 후 캡처 (비동기 처리, 한 번만 실행)
    if (onChartCaptured && captureCanvasRef.current && points.length > 0 && !capturedRef.current) {
      // 이중 requestAnimationFrame + setTimeout으로 렌더링 완전 완료 보장
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (!captureCanvasRef.current || capturedRef.current) return;

            try {
              // console.log(`🖼️ [${chartId}] 이미지 캡처용 Canvas 렌더링 시작:`, {
              //   width: captureDimensions.width,
              //   height: captureDimensions.height,
              //   chartId: chartId
              // });

              // 캡처용 Canvas에 고정 크기로 렌더링 (showStatePoints와 무관하게 일관된 크기)
              drawChart(captureCanvasRef.current, captureDimensions);

              // 캡처용 Canvas에서 이미지 캡처
              const chartData = capturePsychrometricChart(
                captureCanvasRef.current,
                points,
                lines,
                {
                  elevation,
                  saFlow,
                  raFlow,
                  oaFlow: oaFlowMin,
                  eaFlow,
                  taFlow: taFlow || (eaFlow + (oaFlowMax - oaFlowMin)),
                  winterOutdoorTemp,
                  summerOutdoorTemp,
                  raFanType,
                  humidifierType,
                  preheatingCoilType,
                  reheatingCoilType,
                },
                3 // 고해상도 배율 (300 DPI 상당)
              );

              capturedRef.current = true; // 캡처 완료 표시
              //console.log(`📸 습공기 선도 이미지 캡처 완료 (${chartData.image.length} bytes)`);
              onChartCaptured(chartData);
            } catch (error) {
              console.error('습공기 선도 캡처 실패:', error);
            }
          }, 150);
        });
      });
    }
  }, [points, lines, showGrid, showLabels, chartDimensions, elevation, internalShowReferenceLines, internalShowValueSymbols, internalShowRelativeHumidity, internalShowEnthalpy, onChartCaptured, chartId]);

  // points, lines, chartId가 변경되면 캡처 플래그 리셋
  useEffect(() => {
    capturedRef.current = false;
  }, [points, lines, chartId]);

  return (
    <Box id={chartId}>
      <Grid container spacing={1}>
        {/* 좌측: 상태점 정보 (25%) - 이미지 캡처 영역 바깥 */}
        {showStatePoints && (
          <Grid item xs={3}>
            <StatePointsInfo points={points} />
          </Grid>
        )}

        {/* 우측: 습공기 선도 차트 - 이미지 캡처 영역 */}
        <Grid item xs={showStatePoints ? 9 : 12}>

          <Box sx={{ height: '95vh' }}>
            <Box sx={{ ml: 6, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGrid}
                    onChange={(e) => {
                      setShowGrid(e.target.checked);
                      localStorage.setItem('psychrometric-showGrid', JSON.stringify(e.target.checked));
                    }}
                  />
                }
                label="격자 표시"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showLabels}
                    onChange={(e) => {
                      setShowLabels(e.target.checked);
                      localStorage.setItem('psychrometric-showLabels', JSON.stringify(e.target.checked));
                    }}
                  />
                }
                label="라벨 표시"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={internalShowRelativeHumidity}
                    onChange={(e) => {
                      setInternalShowRelativeHumidity(e.target.checked);
                      localStorage.setItem('psychrometric-showRelativeHumidity', JSON.stringify(e.target.checked));
                    }}
                  />
                }
                label="상대습도선 (10%-90%)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={internalShowEnthalpy}
                    onChange={(e) => {
                      setInternalShowEnthalpy(e.target.checked);
                      localStorage.setItem('psychrometric-showEnthalpy', JSON.stringify(e.target.checked));
                    }}
                  />
                }
                label="엔탈피선"
              />
            </Box>

            <Box sx={{
              overflow: 'auto',
              width: '100%',
              height: 'auto'
            }}>

              <Box
                ref={containerRef}
                sx={{
                  width: '90%',
                  aspectRatio: '4 / 3', // A4 가로 방향 최적화 (4:3 비율)
                  maxWidth: '100%',
                  minWidth: '300px',
                  minHeight: '225px', // 300px × 3/4 = 225px (4:3 비율 유지)
                  border: 'none',
                  lineHeight: 0,
                  fontSize: 0,
                  ml: 6,
                  position: 'relative',
                  boxSizing: 'border-box',
                }}
              >

                <canvas
                  ref={canvasRef}
                  width={chartDimensions.width}
                  height={chartDimensions.height}
                  style={{
                    //border: '1px solid #ccc',
                    border: 'none',
                    borderRadius: '4px',
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '4 / 3',
                    display: 'block',
                    verticalAlign: 'top',
                    boxSizing: 'border-box',
                  }}
                />
              </Box>

            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* 이미지 캡처 전용 Canvas (숨김) - 고정 크기로 일관된 이미지 품질 보장 */}
      <canvas
        ref={captureCanvasRef}
        width={captureDimensions.width}
        height={captureDimensions.height}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      />
    </Box>
  );
};

export default PsychrometricChart;
